import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';

import ApiGridpoint from '@/api/ApiGridpoint';
import Utils from '@/api/Utils';

import type { SelectOption } from '@/components/common/types/selectOptionTypes';
import type { CNetGridpointRaw, GridpointListResponse, SubNetworkRaw } from '@/api/ApiTypes';
/*
Example of sub_networks response from server:
{
	"sub_networks": [
		{
			"id": 20,
			"label": "DEFAULT",
			"title": "DEFAULT",
			"coverage": "{...}",
			"quality": "{...}",
			"coverage_calculated_at": "2026-06-04T12:00:00Z",
			"base_stations": [
				{"id": 77},
				{"id": 78},
				{"id": 79},
				{"id": 4},
				{"id": 3},
				{"id": 21},
			],
			"cnets": [
				{
					"id": 70,
					"cnet_number": 1,
					"gridpoints": [
						{
							"id": 473,
							"label": "gp01",
							"latitude": 54711200,
							"longitude": 39743225
						}
					]
				},
				{
					"id": 71,
					"cnet_number": 3,
					"gridpoints": [
						{
							"id": 481,
							"label": "gp06",
							"latitude": 54669560,
							"longitude": 36024353
						}
					]
				}
			]
		},
		{
			"id": 30,
			"label": "T2",
			"title": "T2",
			"coverage": null,
			"quality": null,
			"coverage_calculated_at": null,
			"base_stations": [],
			"cnets": []
		}
	]
}
*/


export const useSubNetworksStore = defineStore('subNetworks', () => {
	const subNetworks = ref<SubNetworkRaw[]>([]);
	const subNetworkSelectedId = ref<number | null>(null);
	const cnetSelectedId = ref<number | null>(null);

	const gridpoints = ref<CNetGridpointRaw[]>([]);

	const getSubNetworkById = (id: number | null): SubNetworkRaw | null => {
		if (id == null) {
			return null;
		}
		const currentSubNetwork = subNetworks.value.find(item => item.id === id);
		return currentSubNetwork ?? null;
	};

	const setDefaultCnetSelectedId = (selectedSubNetwork: SubNetworkRaw | null): void => {
		const cnets = selectedSubNetwork != null ? selectedSubNetwork.cnets : [];
		const firstCnet = cnets[0];
		if (firstCnet != null && cnetSelectedId.value == null) {
			cnetSelectedId.value = firstCnet.id;
		} else if (cnetSelectedId.value != null && !cnets.some(item => item.id === cnetSelectedId.value)) {
			cnetSelectedId.value = firstCnet != null ? firstCnet.id : null;
		}
	};

	const updateSubNetworks = (newSubNetworks: SubNetworkRaw[]): void => {
		// see example above
		const firstNewSubNetwork = newSubNetworks[0];
		if (firstNewSubNetwork != null && subNetworks.value.length === 0 && subNetworkSelectedId.value == null) {
			subNetworkSelectedId.value = firstNewSubNetwork.id;
		} else if (subNetworkSelectedId.value != null && !newSubNetworks.some(item => item.id === subNetworkSelectedId.value)) {
			subNetworkSelectedId.value = firstNewSubNetwork != null ? firstNewSubNetwork.id : null;
		}
		subNetworks.value = newSubNetworks;
		setDefaultCnetSelectedId(getSubNetworkById(subNetworkSelectedId.value));
	};

	const updateSubNetworkSelectedId = (newSubNetworkSelectedId: number): void => {
		if (subNetworks.value.some(item => item.id === newSubNetworkSelectedId)) {
			subNetworkSelectedId.value = newSubNetworkSelectedId;
		} else {
			const firstSubNetwork = subNetworks.value[0];
			subNetworkSelectedId.value = firstSubNetwork != null ? firstSubNetwork.id : null;
		}
	};

	const subNetworkSelectedObject = computed(() => getSubNetworkById(subNetworkSelectedId.value));
	const subNetworkFilterOptions = computed((): SelectOption[] => subNetworks.value.map(item => ({
		value: item.id,
		label: item.title,
	})));

	watch(subNetworkSelectedId, newValue => {
		if (newValue != null) {
			setDefaultCnetSelectedId(getSubNetworkById(newValue));
		}
	});

	watch(cnetSelectedId, () => {
		refreshCurrentGridpoints();
	});

	const cnetSelectOptions = computed((): SelectOption[] => {
		const currentSubNetwork = getSubNetworkById(subNetworkSelectedId.value);
		if (currentSubNetwork == null) {
			return [];
		}
		return currentSubNetwork.cnets.map(item => ({
			value: item.id,
			label: 'C-Net ' + item.cnet_number,
		}));
	});

	const updateCnetSelectedId = (newCnetSelectedId: number): void => {
		if (cnetSelectOptions.value.some(item => item.value === newCnetSelectedId)) {
			cnetSelectedId.value = newCnetSelectedId;
		} else {
			cnetSelectedId.value = null;
		}
	};

	// # TODO gridpoints done: здесь появляется новый уровень вложенности. SN -> c-net_num -> gridpoints
	const gridpointsSelectOptions = computed((): SelectOption<string>[] => [...gridpoints.value.map(item => ({
		value: item.label,
		label: item.label,
	}))].sort(Utils.objectFieldMultiSorter('label')));

	const updateGridpoints = (newGridpoints: CNetGridpointRaw[]): void => {
		gridpoints.value = newGridpoints;
	};

	const clearGridpoints = (): void => {
		updateGridpoints([]);
	};

	const refreshCurrentGridpoints = (): void => {
		// # TODO gridpoints done: переделать на несколько сетей
		clearGridpoints();
		const cnetId = cnetSelectedId.value;
		if (cnetId != null) {
			ApiGridpoint.gridpointGetList(cnetId, (responseData: GridpointListResponse) => {
				if (responseData.status === 'ok' && 'gridpoints' in responseData) {
					updateGridpoints(responseData.gridpoints);
				} else {
					clearGridpoints();
				}
			}, () => {
				clearGridpoints();
			});
		}
	};

	// D3 — SubNetworkRaw has no deleted field; backend returns only active sub_networks.
	const subNetworksPerBaseStations = computed((): Map<number, number[]> => {
		const result = new Map<number, number[]>();
		for (const subNetwork of subNetworks.value) {
			for (const baseStation of subNetwork.base_stations) {
				const existing = result.get(baseStation.id);
				if (existing != null) {
					existing.push(subNetwork.id);
				} else {
					result.set(baseStation.id, [subNetwork.id]);
				}
			}
		}
		return result;
	});

	return {
		subNetworks,
		subNetworkSelectedId,
		subNetworkSelectedObject,
		updateSubNetworks,
		updateSubNetworkSelectedId,
		subNetworkFilterOptions,

		cnetSelectedId,
		cnetSelectOptions,
		updateCnetSelectedId,

		gridpoints,
		gridpointsSelectOptions,
		updateGridpoints,
		refreshCurrentGridpoints,
		clearGridpoints,

		getSubNetworkById,
		subNetworksPerBaseStations,
	};
});
